import api from '../../API/baseURL';
import { handleAuthErrors } from '../../utils/vsCodeUtils';

// PAYG API endpoints
const PAYG_ENDPOINTS = {
  BALANCE: '/payg/balance',
  TIERS: '/payg/tiers',
  CREATE_CHECKOUT: '/payg/checkout',
  QUICK_PURCHASE: '/payg/quick-purchase',
  SAVED_CARD: '/payg/saved-card',
  ADD_CARD: '/payg/add-card',
  UPDATE_CARD: '/payg/update-card',
  AUTO_RELOAD: '/payg/auto-reload',
  TRANSACTIONS: '/payg/transactions',
  CALCULATE_CREDITS: '/payg/calculate-credits'
};

// Get balance and account info
export const getBalance = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(PAYG_ENDPOINTS.BALANCE, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PAYG balance:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get purchase tiers
export const getTiers = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(PAYG_ENDPOINTS.TIERS, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching PAYG tiers:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Create checkout session (first-time purchase)
export const createCheckout = async (amount, successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post(PAYG_ENDPOINTS.CREATE_CHECKOUT, {
      amount,
      success_url: successUrl,
      cancel_url: cancelUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error creating PAYG checkout:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Quick purchase (with saved card)
// tier: 5 | 10 | 25 | 50 | 100 (preset tier amounts)
// amount: number (for custom amount purchases, $1-$500)
export const quickPurchase = async (tier = null, amount = null) => {
  try {
    const token = localStorage.getItem('web_access_token');

    // Build payload - only include one of tier or amount
    let payload;
    if (tier !== null) {
      // Tier should be a number: 5, 10, 25, 50, or 100
      payload = { tier: Number(tier) };
    } else if (amount !== null) {
      payload = { amount: Number(amount) };
    } else {
      throw new Error('Either tier or amount must be provided');
    }

    const response = await api.post(PAYG_ENDPOINTS.QUICK_PURCHASE, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error with PAYG quick purchase:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get saved card info
export const getSavedCard = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(PAYG_ENDPOINTS.SAVED_CARD, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching saved card:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Add card (without purchase)
export const addCard = async (successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post(PAYG_ENDPOINTS.ADD_CARD, {
      success_url: successUrl,
      cancel_url: cancelUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error adding card:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Update card
export const updateCard = async (successUrl, cancelUrl) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.post(PAYG_ENDPOINTS.UPDATE_CARD, {
      success_url: successUrl,
      cancel_url: cancelUrl
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error updating card:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Remove saved card
export const removeSavedCard = async () => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.delete(PAYG_ENDPOINTS.SAVED_CARD, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error removing saved card:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Configure auto-reload
export const configureAutoReload = async (enabled, threshold = null, reloadAmount = null) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const payload = { enabled };
    if (enabled && threshold !== null) {
      payload.threshold = threshold;
    }
    if (enabled && reloadAmount !== null) {
      payload.reload_amount = reloadAmount;
    }
    const response = await api.post(PAYG_ENDPOINTS.AUTO_RELOAD, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error configuring auto-reload:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Get transaction history
export const getTransactions = async (limit = 50, offset = 0) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(`${PAYG_ENDPOINTS.TRANSACTIONS}?limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching transactions:', error);
    handleAuthErrors(error);
    throw error;
  }
};

// Calculate credit preview
export const calculateCredits = async (amount) => {
  try {
    const token = localStorage.getItem('web_access_token');
    const response = await api.get(`${PAYG_ENDPOINTS.CALCULATE_CREDITS}?amount=${amount}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error calculating credits:', error);
    handleAuthErrors(error);
    throw error;
  }
};

export default PAYG_ENDPOINTS;
